import api from '@/api/api';

export const bookings = {
  namespaced: true,
  state: {
    bookingsList: [],
    booking: null,
    loading: false,
    error: null,
    photographersList: [],
    photographersListLoading: false,
    photographersListFailed: false,
  },
  getters: {
    getPhotographersList: (state) => state.photographersList,
    isLoading: (state) => state.loading,
    hasError: (state) => !!state.error,
    allBookings: (state) => state.bookingsList,
    booking: (state) => state.booking,
    pendingBookings: (state) =>
      state.bookingsList.filter((b) => b.status === 'pending'),
    acceptedBookings: (state) =>
      state.bookingsList.filter((b) => b.status === 'accepted'),
    cancelledBookings: (state) =>
      state.bookingsList.filter((b) => b.status === 'cancelled'),
  },
  actions: {
    async fetchAllBookings({ commit }) {
      commit('setLoading', true);
      commit('setError', null);

      try {
        const { data } = await api.fetchAllBookings();
        commit('setBookings', data.data.bookings);
      } catch (error) {
        commit('setError', error);
        throw error;
      } finally {
        commit('setLoading', false);
      }
    },
    async fetchBookingById({ commit }, bookingId) {
      commit('setLoading', true);
      commit('setError', null);

      try {
        const { data } = await api.fetchBookingById(bookingId);
        commit('setBooking', data.data.booking);
      } catch (error) {
        commit('setError', error);
        throw error;
      } finally {
        commit('setLoading', false);
      }
    },
    async createBooking({ commit }, bookingData) {
      commit('setLoading', true);
      commit('setError', null);

      try {
        const { data } = await api.createBooking(bookingData);
        return data.data.booking;
      } catch (error) {
        commit('setError', error);
        throw error;
      } finally {
        commit('setLoading', false);
      }
    },
    async updateBooking({ commit }, { bookingId, payload }) {
      commit('setLoading', true);
      commit('setError', null);

      try {
        const { data } = await api.updateBooking(payload, bookingId);
        commit('updateBooking', data.data.booking);
        return data.booking;
      } catch (error) {
        commit('setError', error);
        throw error;
      } finally {
        commit('setLoading', false);
      }
    },
    async updateBookingStatus({ dispatch }, { bookingId, newStatus }) {
      return dispatch('updateBooking', {
        bookingId,
        payload: { status: newStatus },
      });
    },
    async fetchPhotographersList({ commit, state }) {
      if (state.photographersListLoading) {
        return;
      }
      try {
        commit('setPhotographersListLoading', true);
        const response = await api.fetchPhotographersList();

        const photographersList = response.data.data;

        commit('setPhotographersList', photographersList);
      } catch (error) {
        commit('setPhotographersListFailed', true);
      } finally {
        commit('setPhotographersListLoading', false);
      }
    },
    async payoutBooking({ commit, dispatch, rootState }, bookingId) {
      commit('setLoading', true);
      commit('setError', null);

      try {
        const wpToken = rootState.auth.user?.jwt_token ?? null;
        if (!wpToken) {
          await dispatch('auth/logout', null, { root: true });

          throw new Error('error.badAuthToken');
        }

        const { data } = await api.payoutBooking(bookingId, wpToken);

        await dispatch('global/getUserWalletData', null, { root: true });
        await dispatch('global/getWithdrawRequestList', null, { root: true });

        return data;
      } catch (error) {
        commit('setError', error);
        throw error;
      } finally {
        commit('setLoading', false);
      }
    },
  },
  mutations: {
    setLoading(state, value) {
      state.loading = value;
    },
    setError(state, error) {
      state.error = error;
    },
    setBookings(state, bookings) {
      state.bookingsList = bookings;
    },
    setBooking(state, booking) {
      state.booking = booking;
    },
    updateBooking(state, updatedBooking) {
      const index = state.bookingsList.findIndex(
        (b) => b.id === updatedBooking.id
      );

      if (index !== -1) {
        state.bookingsList.splice(index, 1, updatedBooking);
      }

      if (state.booking?.id === updatedBooking.id) {
        state.booking = updatedBooking;
      }
    },
    setPhotographersList(state, payload) {
      state.photographersList = payload;
    },
    setPhotographersListFailed(state, payload) {
      state.photographersListFailed = payload;
    },
    setPhotographersListLoading(state, payload) {
      state.photographersListLoading = payload;
    },
  },
};
